import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect, Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import useShallowEqualSelector from '../../CustomHooks/useShallowEqualSelector';
import { LOGOUT_TYPE } from '../../utils/AppConstants';
import getLogoutUrl from '../../utils/auth/LogoutManager';
import OIDCAuthManager from '../../utils/auth/OIDCAuthManager';
import ErrorPage from '../ErrorPage/ErrorPage';
import Family from '../Family/Family';
import Personal from '../Personal/Personal';
import StepUpHandler from '../StepUp/StepUpHandler';
import StepUpRequest from '../StepUp/StepUpRequest';
import { TerminateCIAMSession } from '../Timeout/ReducerActions/TerminateCIAMSession';
import ProtectedRoute from './ProtectedRoute';

const App = ({ logger }) => {
    const { isCiamSession, hasAuthenticationError, criticalError, ctxQueryString } = useShallowEqualSelector(
        (state) => ({
            isCiamSession: state.Authentication.isCiamSession,
            hasAuthenticationError: state.Authentication.hasError,
            criticalError: state.Errors.initialLoad,
            ctxQueryString: state.Context.ctxQueryString,
        })
    );

    const dispatch = useDispatch();

    const IDENTITY_CONFIG = {
        // (string): The URL of the OIDC provider.
        authority: window.__ENV__.AUTH_URL,
        // (string): Your client application's identifier as registered with the OIDC provider.
        client_id: window.__ENV__.IDENTITY_CLIENT_ID,
        // The URI of your client application to receive a response from the OIDC provider.
        redirect_uri: window.__ENV__.STEPUP_REDIRECT_URI,
        // (boolean, default: true): Flag to control if additional identity data is loaded from the user info endpoint
        // in order to populate the user's profile.
        loadUserInfo: false,
        // (string, default: 'id_token'): The type of response desired from the OIDC provider.
        response_type: 'code',
        // (string, default: 'openid'): The scope being requested from the OIDC provider.
        scope: 'openid ciam',
        // (string, default: 'openid'): The scope being requested from the OIDC provider for SSO.
        sso_scope: 'openid ciam gb_member_profile',
    };

    const oidc = new OIDCAuthManager(IDENTITY_CONFIG);

    // Redirect user to sign in page when authentication fails
    useEffect(() => {
        if (hasAuthenticationError) {
            logger.error('User authentication failed.');
            if (isCiamSession) {
                dispatch(TerminateCIAMSession());
            }
            window.location.assign(getLogoutUrl(LOGOUT_TYPE.signout, ctxQueryString));
        }
    }, [hasAuthenticationError, logger, dispatch, isCiamSession]);

    if (criticalError.hasError) {
        return (
            <Router>
                <ErrorPage logger={logger} error={criticalError} />
            </Router>
        );
    }

    return (
        <Router>
            <Switch>
                <Route path="/gb/member-profile-2/public/auth">
                    <StepUpRequest oidc={oidc} />
                </Route>
                <Route path="/gb/member-profile-2/public/handleauth">
                    <StepUpHandler logger={logger} oidc={oidc} />
                </Route>
                <ProtectedRoute logger={logger} path="/gb/member-profile-2/personal">
                    <Personal />
                </ProtectedRoute>
                <ProtectedRoute logger={logger} path="/gb/member-profile-2/family">
                    <Family />
                </ProtectedRoute>
                <ProtectedRoute logger={logger}>
                    <Redirect to="/gb/member-profile-2/personal" />
                </ProtectedRoute>
            </Switch>
        </Router>
    );
};

export default App;
